import type { FieldValues, Path, UseFormSetValue } from 'react-hook-form'

/**
 * How a field goes back to "not answered". Stored as a kind rather than as a
 * literal so every reset writes a fresh `[]` instead of sharing one array
 * instance across fields and across forms.
 */
type ResetKind = 'clear' | 'off' | 'empty'

/** A field and the value it returns to when the property type changes. */
export type FieldReset = readonly [field: string, reset: ResetKind]

/**
 * Type-specific answers cleared when the property type changes.
 *
 * One list, imported by every form that lets the type change — the wizard's
 * step 1 and the property edit sheet. It lives in its own module because those
 * two used to keep hand-copied versions of it, and the sheet's copy stopped at
 * `smokingAllowed`: an office converted to a flat kept its building class,
 * ceiling height, commercial parking and garage kind — invisible on screen,
 * resent on every save and exported to the marketplaces from there. A field can
 * now only be added to both places at once.
 */
export const TYPE_SPECIFIC_RESETS: readonly FieldReset[] = [
	['housingType',        'clear'],
	['roomLocation',       'clear'],
	['roomsInApartment',   'clear'],
	['roomHousingType',    'clear'],
	['bedCount',           'clear'],
	['sleepingPlaces',     'clear'],
	['eventsAllowed',      'off'],
	['fireplace',          'off'],
	['houseKind',          'clear'],
	// Only a house is asked what its walls are made of; a block of flats
	// answers through buildingType.
	['wallsType',          'clear'],
	['sauna',              'off'],
	['pool',               'off'],
	['terrace',            'off'],
	['plotArea',           'clear'],
	// The land category is a question only a plot is asked.
	['landCategory',       'clear'],
	['bathroomLocations',  'empty'],
	['communications',     'empty'],
	['transportAccess',    'empty'],
	['nearbyInfrastructure', 'empty'],
	['floor',              'clear'],
	['rooms',              'clear'],
	// Adjacent or isolated describes a whole flat, not a room inside one.
	['roomType',           'clear'],
	['balcony',            'off'],
	['loggia',             'off'],
	['wardrobe',           'off'],
	['totalArea',          'clear'],
	['livingArea',         'clear'],
	['kitchenSpace',       'clear'],
	['bathroomType',       'clear'],
	['renovation',         'clear'],
	['furniture',          'empty'],
	['appliances',         'empty'],
	['internet',           'empty'],
	['buildingType',       'clear'],
	['concierge',          'off'],
	['chute',              'off'],
	['gas',                'off'],
	['yearBuilt',          'clear'],
	['totalFloors',        'clear'],
	['passengerElevators', 'clear'],
	['freightElevators',   'clear'],
	['yard',               'empty'],
	['parking',            'empty'],
	// A flat and a house have parking of their own; a garage and commercial
	// premises are asked a different question.
	['parkingType',        'clear'],
	['maxTenants',         'clear'],
	['childrenAllowed',    'clear'],
	['petsAllowed',        'clear'],
	['smokingAllowed',     'clear'],

	// Коммерческие ответы: объект, переставший быть коммерческим, не должен
	// унести с собой класс здания или тип входа. Сервер чистит их тоже,
	// здесь — чтобы форма не показывала лишнего до сохранения.
	['onlineViewing',      'clear'],
	['multipleFloors',     'clear'],
	['officeLayouts',      'empty'],
	['partialRent',        'clear'],
	['ceilingHeight',      'clear'],
	['finishing',          'clear'],
	['powerCapacity',      'clear'],
	['powerCapacityExpandable', 'clear'],
	['commercialBuildingType', 'clear'],
	['buildingClass',      'clear'],
	['commercialParking',  'clear'],
	['parkingFree',        'clear'],
	['parkingForTrucks',   'clear'],
	['parkingSpaces',      'clear'],
	['entrance',           'clear'],
	['separateEntrance',   'clear'],
	['commercialHeating',  'clear'],
	['retailPlacement',    'clear'],
	['additionalCategories', 'empty'],
	['garageKind',         'clear'],
	['garageType',         'clear'],
	['parkingSpaceType',   'clear'],
	['security',           'clear'],
	['seatsCount',         'clear'],
	['weekendsOpen',       'clear'],
	['aroundTheClock',     'clear'],
	['workplaceStatus',    'clear'],
	['privateOfficeArea',  'clear'],
	['privateOfficeSeats', 'clear'],
	['amenitiesInclusion', 'clear'],
	['keyAmenities',       'empty'],
	['equipment',          'empty'],
	['foodAndDrinks',      'empty'],
	['coworkingServices',  'empty'],
	['coworkingExtras',    'empty'],
]

/**
 * Rent terms only a commercial let is asked about (wizard step 3).
 *
 * Kept apart from the list above because only the wizard owns these fields —
 * the edit sheet has no rent terms at all — but they die with the type just the
 * same. Without this the step-3 answers survived a type change and were saved
 * onto a flat: `rentUnit = PER_SQM_YEAR`, `leaseKind = SUBLEASE`, a
 * three-month deposit.
 *
 * `otherUtilitiesPayment` is deliberately absent: every type is asked it, and a
 * sum agreed for the utilities survives a correction of the type just as the
 * rent itself does.
 */
export const COMMERCIAL_RENT_TERM_RESETS: readonly FieldReset[] = [
	['rentUnit',                  'clear'],
	['leaseKind',                 'clear'],
	['agentCommissionPercent',    'clear'],
	['minLeaseMonths',            'clear'],
	['depositMonths',             'clear'],
	['rentHolidays',              'clear'],
	['utilitiesIncluded',         'clear'],
	['operatingExpensesIncluded', 'clear'],
]

/**
 * Writes every listed field back to its "not answered" value. Field names are
 * plain strings — the lists are shared by two forms with different value types
 * — so the cast is unavoidable and is confined to this one line.
 */
export function applyFieldResets<T extends FieldValues>(
	setValue: UseFormSetValue<T>,
	...lists: readonly (readonly FieldReset[])[]
): void {
	for (const list of lists) {
		for (const [field, reset] of list) {
			const value = reset === 'empty' ? [] : reset === 'off' ? false : undefined
			setValue(field as Path<T>, value as never)
		}
	}
}
